import { useState, useEffect } from 'react'
import { clsx } from 'clsx'
import { TransferWindowState, type GameweekStatus } from '../types'
import styles from './DeadlineCard.module.css'

type DeadlineCardProps = {
  gwStatus: GameweekStatus
  onAction: () => void
}

// Days-aware countdown parts. Mirrors the shared formatCountdown semantics so this card
// reads like the league card / squad-picker banners (e.g. "4d 17h", not "113h 20m").
function diffParts(targetIso: string): { d: number; h: number; m: number } | null {
  const diff = new Date(targetIso).getTime() - Date.now()
  if (diff <= 0) return null
  const totalMinutes = Math.floor(diff / 60_000)
  const totalHours   = Math.floor(totalMinutes / 60)
  return { d: Math.floor(totalHours / 24), h: totalHours % 24, m: totalMinutes % 60 }
}

export function DeadlineCard({ gwStatus, onAction }: DeadlineCardProps) {
  // Re-render every 30s so the countdown stays fresh.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000)
    return () => clearInterval(id)
  }, [])

  const { currentGameweek, windowState, windowChangesAt } = gwStatus
  const isOpen = windowState === TransferWindowState.Open
  const parts = windowChangesAt ? diffParts(windowChangesAt) : null
  const isPreSeason = currentGameweek === null

  return (
    <div className={styles.card}>
      <div className={styles.head}>
        <span className={styles.title}>Próxima rodada</span>
      </div>
      <div className={styles.body}>
        {isPreSeason ? (
          <>
            <div className={clsx(styles.state, styles.stateClosed)}>
              <span className={clsx(styles.dot, styles.dotClosed)} />
              Aguardando início
            </div>
            <p className={styles.pre}>A competição ainda não começou.</p>
          </>
        ) : (
          <>
            <div className={clsx(styles.state, isOpen ? styles.stateOpen : styles.stateClosed)}>
              <span className={clsx(styles.dot, isOpen ? styles.dotOpen : styles.dotClosed)} />
              {isOpen ? 'Janela aberta' : 'Janela fechada'}
            </div>
            {parts ? (
              <div className={styles.count}>
                {parts.d > 0 ? (
                  <>
                    {parts.d}<span className={styles.u}>d</span>{' '}
                    {parts.h}<span className={styles.u}>h</span>
                  </>
                ) : parts.h > 0 ? (
                  <>
                    {parts.h}<span className={styles.u}>h</span>{' '}
                    {parts.m}<span className={styles.u}>m</span>
                  </>
                ) : (
                  <>{parts.m}<span className={styles.u}>m</span></>
                )}
              </div>
            ) : (
              <div className={styles.count}>—</div>
            )}
            <div className={styles.sub}>
              {isOpen ? 'até a janela fechar' : 'até a janela reabrir'} · Rodada {currentGameweek}
            </div>
          </>
        )}

        <button className={styles.cta} onClick={onAction}>
          {isPreSeason ? 'Montar escalação →' : isOpen ? 'Fazer transferências →' : 'Ver escalação →'}
        </button>
      </div>
    </div>
  )
}
